/**
 * @file ActivityTab.tsx
 * @brief Super Admin → Analytics → Activity: sessions, language pairs, top
 *        users and companies, user and company sign-ups.
 *
 * @details
 * The operational half of the old Analytics page, unchanged, now a tab
 * (W22, 25-10-2026). The old Revenue / Cost / Profit tiles and chart were
 * removed: they read /analytics/revenue*, which books meetings, Notes and
 * plans at £0 cost and mixes USD cost with local revenue (B25 contract §7).
 * Money now lives on the Revenue & margin and Costs tabs (B25).
 *
 * The window is the page's period: `endDate` = the exclusive `to` − 1 ms,
 * as these older endpoints compare inclusively.
 *
 * @author DES
 * @date 26-05-2026
 * @updated 25-10-2026 — W22: a tab of the new Analytics; money tiles removed
 */

import { useMemo, useState } from "react";
import { Activity, Building2, Languages, Users } from "lucide-react";
import { formatDurationSeconds, formatLanguagePair, formatMoneyMinor } from "@/shared/utils/formatters";
import {
  useCompanyGrowth,
  useLanguageStats,
  useSessionStats,
  useSessionTrend,
  useSessionsPerCompany,
  useSessionsPerUser,
  useUserGrowth,
} from "./api";
import { flatAnalyticsFilterControlClass } from "./components/AnalyticsRangeBar";
import { GridCell, GridCells } from "./components/GridBlock";
import { StatCard } from "./components/StatCard";
import { TrendChart } from "./components/TrendChart";
import { CompanyPicker } from "@/features/companies/components/CompanyPicker";
import { cn } from "@/shared/theme/cn";
import { SkeletonRows } from "@/components/ui/Skeleton";
import type { BusinessQueryWindow } from "@/features/business-analytics/businessMath";

export function ActivityTab({ win }: { win: BusinessQueryWindow }) {
  const [companyId, setCompanyId] = useState<string | null>(null);
  const isCompanyScoped = Boolean(companyId);

  const baseRange = useMemo(
    () => ({
      startDate: win.from,
      endDate: new Date(new Date(win.to).getTime() - 1).toISOString(),
      companyId: companyId ?? undefined,
    }),
    [win.from, win.to, companyId],
  );
  const trendRange = useMemo(() => ({ ...baseRange, groupBy: win.groupBy }), [baseRange, win.groupBy]);

  const sessionStats = useSessionStats(baseRange);
  const sessionTrend = useSessionTrend(trendRange);
  const languages = useLanguageStats({ ...baseRange, limit: 10 });
  const perUser = useSessionsPerUser({ ...baseRange, limit: 10 });
  const perCompany = useSessionsPerCompany({ ...baseRange, limit: 10 }, { enabled: !isCompanyScoped });
  const userGrowth = useUserGrowth(trendRange, { enabled: !isCompanyScoped });
  const companyGrowth = useCompanyGrowth(trendRange, { enabled: !isCompanyScoped });

  return (
    <>
      <GridCells className="grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <GridCell padded={false}>
          <StatCard
            variant="cell"
            label="Sessions"
            icon={Activity}
            value={Number(sessionStats.data?.total_sessions ?? 0).toLocaleString()}
            hint={
              sessionStats.data
                ? `Avg ${formatDurationSeconds(sessionStats.data.avg_duration_seconds)} · Total ${formatDurationSeconds(sessionStats.data.total_duration_seconds)}`
                : "—"
            }
            isLoading={sessionStats.isLoading}
          />
        </GridCell>
        <GridCell padded={false} className="flex items-center">
          <CompanyPicker
            value={companyId}
            onChange={setCompanyId}
            placeholder="All companies"
            size="sm"
            triggerClassName={flatAnalyticsFilterControlClass}
          />
        </GridCell>
      </GridCells>

      <GridCell>
        <TrendChart
          variant="cell"
          title="Sessions trend"
          subtitle={`Sessions per ${win.groupBy}`}
          data={sessionTrend.data ?? []}
          isLoading={sessionTrend.isLoading}
          series={[{ dataKey: "session_count", label: "Sessions", color: "hsl(var(--brand))" }]}
        />
      </GridCell>

      <GridCell padded={false}>
        <div className="space-y-1 px-4 py-4 md:px-5">
          <h3 className="flex items-center gap-2 text-base font-semibold tracking-tight">
            <Languages className="h-4 w-4 text-muted-foreground" />
            Top language pairs
          </h3>
          <p className="text-sm text-muted-foreground">Ordered by session count for the selected period.</p>
        </div>
        {languages.isLoading ? (
          <SkeletonRows />
        ) : !languages.data || languages.data.length === 0 ? (
          <p className="px-4 py-6 text-center text-sm text-muted-foreground">No language data for this period yet.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wide text-muted-foreground">
              <tr className="border-t border-border/60">
                <th className="px-4 py-2.5 text-left font-medium md:px-5">Pair</th>
                <th className="px-4 py-2.5 text-right font-medium md:px-5">Sessions</th>
                <th className="px-4 py-2.5 text-right font-medium md:px-5">Total duration</th>
              </tr>
            </thead>
            <tbody>
              {languages.data.map((row, idx) => (
                <tr key={`${row.source_language}-${row.target_language}-${idx}`} className="border-t border-border/60">
                  <td className="px-4 py-2.5 md:px-5">{formatLanguagePair(row.source_language, row.target_language)}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums md:px-5">{Number(row.session_count).toLocaleString()}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums md:px-5">{formatDurationSeconds(row.total_duration_seconds)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </GridCell>

      <GridCells className={cn("grid-cols-1", !isCompanyScoped && "lg:grid-cols-2")}>
        <GridCell padded={false}>
          <h3 className="flex items-center gap-2 px-4 py-4 text-base font-semibold tracking-tight md:px-5">
            <Users className="h-4 w-4 text-muted-foreground" />
            Top users by session volume
          </h3>
          {perUser.isLoading ? (
            <SkeletonRows twoLine />
          ) : !perUser.data || perUser.data.length === 0 ? (
            <p className="px-4 py-6 text-center text-sm text-muted-foreground">No user data for this period.</p>
          ) : (
            <ul className="divide-y divide-border/60 border-t border-border/60">
              {perUser.data.map((u) => (
                <li key={u.user_id} className="flex items-center justify-between px-4 py-2.5 md:px-5">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">
                      {[u.first_name, u.last_name].filter(Boolean).join(" ") || u.email || "—"}
                    </p>
                    <p className="truncate text-xs text-muted-foreground">{u.email ?? "—"}</p>
                  </div>
                  <div className="ml-3 text-right">
                    <p className="text-sm font-semibold tabular-nums">{Number(u.session_count).toLocaleString()}</p>
                    <p className="text-xs tabular-nums text-muted-foreground">{formatDurationSeconds(u.total_duration_seconds)}</p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </GridCell>

        {!isCompanyScoped ? (
          <GridCell padded={false}>
            <h3 className="flex items-center gap-2 px-4 py-4 text-base font-semibold tracking-tight md:px-5">
              <Building2 className="h-4 w-4 text-muted-foreground" />
              Top companies by session volume
            </h3>
            {perCompany.isLoading ? (
              <SkeletonRows twoLine />
            ) : !perCompany.data || perCompany.data.length === 0 ? (
              <p className="px-4 py-6 text-center text-sm text-muted-foreground">No company data for this period.</p>
            ) : (
              <ul className="divide-y divide-border/60 border-t border-border/60">
                {perCompany.data.map((c) => (
                  <li key={c.company_id} className="flex items-center justify-between px-4 py-2.5 md:px-5">
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium">{c.company_name ?? "—"}</p>
                      <p className="truncate text-xs text-muted-foreground">
                        {formatDurationSeconds(c.total_duration_seconds)} total duration
                      </p>
                    </div>
                    <div className="ml-3 text-right">
                      <p className="text-sm font-semibold tabular-nums">{Number(c.session_count).toLocaleString()}</p>
                      <p className="text-xs tabular-nums text-muted-foreground" title="Interpretation session cost only">
                        {formatMoneyMinor(c.total_cost_cents, "GBP")}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </GridCell>
        ) : null}
      </GridCells>

      {!isCompanyScoped ? (
        <GridCells className="grid-cols-1 lg:grid-cols-2">
          <GridCell>
            <TrendChart
              variant="cell"
              title="User signups"
              subtitle={
                userGrowth.data
                  ? `${Number(userGrowth.data.totals.total_users ?? 0).toLocaleString()} users total · ${Number(userGrowth.data.totals.total_active ?? 0).toLocaleString()} active`
                  : ""
              }
              data={userGrowth.data?.trends ?? []}
              isLoading={userGrowth.isLoading}
              series={[{ dataKey: "count", label: "New users", color: "hsl(var(--brand))" }]}
            />
          </GridCell>
          <GridCell>
            <TrendChart
              variant="cell"
              title="Company signups"
              subtitle={
                companyGrowth.data
                  ? `${Number(companyGrowth.data.totals.total_companies ?? 0).toLocaleString()} companies total · ${Number(companyGrowth.data.totals.total_active ?? 0).toLocaleString()} active`
                  : ""
              }
              data={companyGrowth.data?.trends ?? []}
              isLoading={companyGrowth.isLoading}
              series={[{ dataKey: "count", label: "New companies", color: "hsl(var(--info))" }]}
            />
          </GridCell>
        </GridCells>
      ) : null}
    </>
  );
}
